import React, { useInsertionEffect } from "react";

import { injectTranscendAirgap } from "./loader";

/**
 * Injects Transcend's `airgap.js` consent gate (see `utils/transcend.ts`).
 *
 * Renders nothing — it exists only for the effect. It must be rendered in BOTH
 * the SSR tree (`renderer.tsx`) and the client tree (`index.tsx`) at the same
 * position: even a null-rendering component fiber shifts React's `useId`
 * tree-context, so leaving it out of one tree offsets every `useId` in
 * `<Template>` and breaks hydration.
 *
 * Why `useInsertionEffect` (yes, it's nominally a CSS-in-JS hook):
 *   - It must run AFTER hydration. `injectTranscendAirgap` mutates `<head>`
 *     (it inserts a `<script>`). Doing that before `hydrateRoot` finishes shifts
 *     the positions of the server-rendered `<head>` nodes, so React hydrates a
 *     component onto the wrong DOM node (e.g. the JsonLD `<script>`) and throws
 *     a hydration mismatch. Effects fire only after the commit.
 *   - It must still run BEFORE `GoogleTagManagerBlock` (and OneTrust) inject
 *     their tags, so airgap lands in the DOM first and can gate what GTM pulls
 *     in once live. `useInsertionEffect` fires before all layout AND passive
 *     effects, so it beats GTM's `useEffect` regardless of tree order.
 *   - Unlike `useLayoutEffect`, it runs only on the client with no "does
 *     nothing on the server" warning, so no isomorphic wrapper is needed. Its
 *     caveats (no state updates, refs not yet attached, don't depend on the
 *     DOM-update timing) don't apply — we only do a raw `document.head` insert.
 */
export const TranscendAirgapLoader = (): React.ReactNode => {
    useInsertionEffect(() => {
        injectTranscendAirgap();
    }, []);
    return null;
};
